import type { QueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import type { WebSocketEventRouter } from '@/shared/websocket/router';
import type { WebSocketEnvelope } from '@/shared/websocket/types';

// Схема для валидации события прогресса синхронизации биллинга
const BillingSyncProgressSchema = z.object({
  billing_id: z.number(),
  billing_name: z.string(),
  total_customers: z.number(),
  processed_customers: z.number(),
  last_processed_id: z.string().optional().nullable(),
  status: z.enum(['running', 'success', 'error']),
  error_message: z.string().optional().nullable(),
  started_at: z.number(),
  updated_at: z.number(),
  progress_percent: z.number(),
});

export interface BillingEventsDependencies {
  queryClient: QueryClient;
}

/**
 * Регистрирует обработчики WebSocket событий для биллинга
 */
export function registerBillingWebSocketHandlers(
  router: WebSocketEventRouter,
  { queryClient }: BillingEventsDependencies,
): () => void {
  const disposers: Array<() => void> = [];

  // Обработчик события прогресса синхронизации биллинга
  const handleBillingSyncProgress = (event: WebSocketEnvelope) => {
    const result = BillingSyncProgressSchema.safeParse(event.data);
    if (!result.success) {
      if (import.meta.env.DEV) {
        console.warn('[BillingEvents] Невалидные данные billing.sync.progress', event.data, result.error);
      }
      return;
    }

    const { billing_id, status } = result.data;

    // Инвалидировать кеш биллингов для обновления статуса
    queryClient.invalidateQueries({ queryKey: ['billingModules'] });
    queryClient.invalidateQueries({ queryKey: ['billingModules', billing_id] });
    queryClient.invalidateQueries({ queryKey: ['billingModules', billing_id, 'progress'] });

    if (import.meta.env.DEV) {
      console.log('[BillingEvents] Прогресс синхронизации биллинга', billing_id, result.data);
    }
  };

  // Регистрируем обработчик события billing.sync.progress
  disposers.push(router.on('billing.sync.progress', handleBillingSyncProgress));

  return () => {
    disposers.forEach((dispose) => dispose());
  };
}

